<script lang="ts">
  import { generateBackstory, completeOnboarding, completeUserOnboarding, processKnowledgeFile, saveKnowledgeEntry, processOutreachFile, saveOutreachEntry } from '../lib/api';
  import { uploadFundDocument, setFundAutoDrafts,
    FUND_AUTO_DRAFT_CONSENT_VERSION } from '../lib/api/fund-ir';

  // `preview` replays the real wizard for an account that has already finished
  // it: every write is blocked and fields after the consent step are optional,
  // so you can walk both branches end to end without touching account data.
  // It is the only way to see
  // this flow on an existing tenant — running it for real would overwrite that
  // company's `business_config`, and a tenant that already has one is routed to
  // the two-step teammate wizard, where the workflow chooser never renders.
  let { userName = '', mode = 'full', initialWorkflow = 'sales', accountTypeLocked = false, preview = false, onComplete = () => {}, onExit = () => {} }:
    { userName: string; mode: 'full' | 'user'; initialWorkflow?: 'sales' | 'fund'; accountTypeLocked?: boolean; preview?: boolean; onComplete: (workflow: 'sales' | 'fund') => void; onExit?: () => void } = $props();

  // Which workflow this account is. corporate -> clients are Sales prospects;
  // fund -> clients are investors/LPs (plus portfolio companies). The pair is
  // 1:1 in v1, so step 3 asks once. Chosen here, sent to the backend, and
  // recorded on the organization at Finish. New members inherit that choice.
  type UserType = 'corporate' | 'fund';
  let userType = $state<UserType | null>(null);
  $effect(() => { if (mode === 'user' || (accountTypeLocked && !preview)) userType = initialWorkflow === 'fund' ? 'fund' : 'corporate'; });
  const isFund = $derived(userType === 'fund');

  // Fund reuses the Sales wizard's identity, profile, firm, context, partner,
  // and materials screens. Sales-only expert, backstory, outreach, and
  // knowledge-base screens remain outside the Fund path.
  const visibleSteps = $derived<number[]>(
    mode === 'user' ? [1, 2]
      : isFund ? [1, 2, 3, 4, 6, 10]
      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  );
  let stepIndex = $state(0);
  let autoDraftsChoice = $state(false);
  let autoDraftsSaved = false;
  // Clamp rather than index out of bounds: picking IR on step 3 shortens the
  // list under a user who may already be further along on a back-navigation.
  const step = $derived(visibleSteps[Math.min(stepIndex, visibleSteps.length - 1)]);
  const totalSteps = $derived(visibleSteps.length);
  const stepNumber = $derived(Math.min(stepIndex, visibleSteps.length - 1) + 1);

  // Step 1: Name — initialised from prop, user can edit
  let displayName = $state('');
  $effect(() => { if (!displayName && userName) displayName = userName; });

  // Step 2: Your persona (human writing style)
  let userBackground = $state('');
  // Keep this optional field empty so an example does not become the user's
  // claimed voice. The backend supplies a neutral style when it is blank.
  let userWritingStyle = $state('');

  // Step 3: Company
  let companyName = $state('');
  let productName = $state('');
  let productDescription = $state('');

  // Fund onboarding collects each vehicle separately, then writes a readable
  // summary into the existing single-value product fields. This keeps every
  // fund visible to downstream prompts without changing the shared API.
  type FundDraft = { id: number; name: string; strategy: string };
  let funds = $state<FundDraft[]>([]);
  let nextFundId = 1;

  function addFund() {
    funds = [...funds, { id: nextFundId++, name: '', strategy: '' }];
  }

  function removeFund(id: number) {
    funds = funds.filter(fund => fund.id !== id);
  }

  function fundOffering() {
    const entries = funds.map(fund => ({ name: fund.name.trim(), strategy: fund.strategy.trim() })).filter(fund => fund.name);
    return {
      name: entries.map(fund => fund.name).join('; '),
      description: entries.length === 0 ? '' : entries.length === 1
        ? entries[0].strategy
        : entries.filter(fund => fund.strategy).map(fund => `${fund.name}: ${fund.strategy}`).join('\n'),
    };
  }

  // Step 4: Target audience (Sales) / the fund's own profile (IR)
  let industry = $state('');
  let targetAudience = $state('');
  let personalEmailsAsClients = $state(false);

  // Step 4, fund branch. `sectorFocus` and `targetLpTypes` are sent structured
  // AND joined into industry/target_audience server-side, so the eight existing
  // consumers of those two keep working — see routers/onboarding.py.
  const SECTOR_OPTIONS = [
    'Mining & metals', 'Energy transition', 'Infrastructure', 'Real estate',
    'Software', 'Healthcare', 'Fintech',
  ];
  const LP_OPTIONS = [
    'Family offices', 'Pensions', 'Endowments', 'Foundations', 'Sovereign wealth',
    'Insurers', 'Fund of funds', 'Corporates', 'HNWIs',
  ];
  let sectorFocus = $state<string[]>([]);
  let targetLpTypes = $state<string[]>([]);
  let customSector = $state('');
  let customLpType = $state('');

  function toggleFrom(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter(v => v !== value) : [...list, value];
  }
  function addCustomSector() {
    const v = customSector.trim();
    if (v && !sectorFocus.includes(v)) sectorFocus = [...sectorFocus, v];
    customSector = '';
  }
  function addCustomLpType() {
    const value = customLpType.trim();
    if (value && !targetLpTypes.includes(value)) targetLpTypes = [...targetLpTypes, value];
    customLpType = '';
  }

  // Step 5: Expert types
  const expertOptions = [
    'Geologist', 'Metallurgist', 'Data Scientist', 'Chemist', 'Biologist', 'Physician',
    'Mechanical Engineer', 'Software Engineer', 'Chemical Engineer', 'Civil Engineer',
    'Mining Engineer', 'ML Engineer', 'Environmental Scientist',
  ];
  let selectedExperts = $state<string[]>([]);
  let customExpert = $state('');

  // Step 6: Partner name is chosen by the user, with examples in the UI.
  let personaName = $state('');

  // Step 7: Backstory
  let backstoryMode = $state<'dark' | 'light'>('dark');
  let backstory = $state('');
  let generating = $state(false);
  let hasGenerated = $state(false);

  // Step 8: Outreach objective
  let outreachObjective = $state<'call' | 'self_serve'>('call');
  let selfServeUrl = $state('');

  // Step 9: Knowledge base (optional)
  let kbFile: File | null = $state(null);
  let kbProcessing = $state(false);
  let kbEntries = $state<{ title: string; summary: string; when_to_use: string; filename: string; access: string }[]>([]);
  let kbEditIdx = $state(-1);

  // Step 10: Marketing materials (optional)
  let mkFile: File | null = $state(null);
  let mkProcessing = $state(false);
  let mkEntries = $state<{ title: string; summary: string; when_to_attach: string; filename: string; file: File }[]>([]);
  let mkEditIdx = $state(-1);
  let fundFiles = $state<File[]>([]);
  let collateralFiles = $state<File[]>([]);
  let setupSaved = false;
  let uploadRecovery = $state(false);

  function queueFundFiles(event: Event, category: 'fund' | 'collateral') {
    const input = event.currentTarget as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    if (selected.some(file => file.size > 20 * 1024 * 1024)) {
      error = 'Each document must be 20 MB or smaller.';
    } else if (category === 'fund') {
      fundFiles = [...fundFiles, ...selected];
      error = '';
    } else {
      collateralFiles = [...collateralFiles, ...selected];
      error = '';
    }
    input.value = '';
  }

  let saving = $state(false);
  let tosAccepted = $state(false);
  let error = $state('');

  function toggleExpert(e: string) {
    if (selectedExperts.includes(e)) {
      selectedExperts = selectedExperts.filter(x => x !== e);
    } else {
      selectedExperts = [...selectedExperts, e];
    }
  }

  function addCustomExpert() {
    const trimmed = customExpert.trim();
    if (trimmed && !selectedExperts.includes(trimmed)) {
      selectedExperts = [...selectedExperts, trimmed];
      customExpert = '';
    }
  }

  async function doGenerate() {
    if (preview) return;
    if (!personaName || selectedExperts.length === 0) return;
    generating = true;
    error = '';
    try {
      const res = await generateBackstory({
        user_type: userType ?? 'corporate',
        mode: backstoryMode,
        persona_name: personaName,
        expert_type: selectedExperts[0].toLowerCase(),
        company_name: companyName,
        product_name: productName,
        product_description: productDescription,
        target_audience: targetAudience,
        industry: industry,
      });
      backstory = res.backstory;
      hasGenerated = true;
    } catch (e: any) {
      error = e.message || 'Failed to generate backstory';
    } finally {
      generating = false;
    }
  }

  async function switchMode(mode: 'dark' | 'light') {
    backstoryMode = mode;
    await doGenerate();
  }

  async function processKb() {
    if (preview) return;
    if (!kbFile) return;
    kbProcessing = true;
    try {
      const r = await processKnowledgeFile(kbFile, '');
      kbEntries = [...kbEntries, { ...r, access: 'on-demand' }];
      kbFile = null;
      // Reset file input
      const el = document.getElementById('kb-onboard-file') as HTMLInputElement;
      if (el) el.value = '';
    } catch (e: any) {
      error = 'Failed to process: ' + (e.message || '');
    }
    kbProcessing = false;
  }

  function removeKb(idx: number) {
    kbEntries = kbEntries.filter((_, i) => i !== idx);
  }

  async function processMk() {
    if (preview) return;
    if (!mkFile) return;
    mkProcessing = true;
    try {
      const r = await processOutreachFile(mkFile, '');
      mkEntries = [...mkEntries, { ...r, when_to_attach: r.when_to_attach, file: mkFile }];
      mkFile = null;
      const el = document.getElementById('mk-onboard-file') as HTMLInputElement;
      if (el) el.value = '';
    } catch (e: any) {
      error = 'Failed to process: ' + (e.message || '');
    }
    mkProcessing = false;
  }

  function removeMk(idx: number) {
    mkEntries = mkEntries.filter((_, i) => i !== idx);
  }

  async function finish(skipPendingUploads = false) {
    if (preview) { onExit(); return; }
    if (mode !== 'user' && userType === null) {
      error = 'Choose Corporate or Fund in organization details.';
      return;
    }
    saving = true;
    error = '';
    uploadRecovery = false;
    try {
      if (!setupSaved && isFund) {
        backstoryMode = 'light';
        // Step 6 supplies the name. Use only confirmed setup fields here;
        // onboarding must not invent a biography or require a model call.
        backstory = `${personaName.trim()} supports investor relations at ${companyName.trim()}.`;
      }
      if (mode === 'user') {
        await completeUserOnboarding({ user_name: displayName, user_background: userBackground, user_writing_style: userWritingStyle });
      } else {
        // Build expertise from selected expert types
        const expertise = selectedExperts.map(e => e.toLowerCase()).join(', ');
        const offering = isFund ? fundOffering() : { name: productName, description: productDescription };
        if (!setupSaved) await completeOnboarding({
          user_name: displayName,
          user_background: userBackground,
          user_writing_style: userWritingStyle,
          company_name: companyName,
          product_name: offering.name,
          product_description: offering.description,
          industry: industry,
          target_audience: targetAudience,
          expert_types: selectedExperts.map(e => e.toLowerCase()),
          persona_name: personaName,
          persona_backstory: backstory,
          persona_backstory_mode: backstoryMode,
          ai_assistant_expertise: expertise,
          outreach_objective: outreachObjective,
          self_serve_url: outreachObjective === 'self_serve' ? selfServeUrl.trim() : '',
          personal_emails_as_clients: personalEmailsAsClients ? 'true' : 'false',
          user_type: userType ?? 'corporate',
          sector_focus: sectorFocus,
          target_lp_types: targetLpTypes,
          funds: isFund ? funds.map(({ name, strategy }) => ({ name: name.trim(), strategy: strategy.trim() })).filter(fund => fund.name) : [],
        });
        setupSaved = true;

        if (isFund && !skipPendingUploads) {
          // Upload only after the profile exists. Remove each successful file
          // from the queue so retrying a partial upload cannot duplicate it.
          while (fundFiles.length) {
            await uploadFundDocument(fundFiles[0], 'fund');
            fundFiles = fundFiles.slice(1);
          }
          while (collateralFiles.length) {
            await uploadFundDocument(collateralFiles[0], 'collateral');
            collateralFiles = collateralFiles.slice(1);
          }
        } else if (!isFund) {
          // These routes write to the shared Sales knowledge/collateral store.
          // Drained like the Fund queues above: neither route is idempotent, so
          // an entry that is already stored must leave the list before the next
          // one is attempted, or a retry after a mid-loop failure saves it twice.
          while (kbEntries.length) {
            const kb = kbEntries[0];
            await saveKnowledgeEntry({
              title: kb.title,
              summary: kb.summary,
              when_to_use: kb.when_to_use,
              source: kb.filename,
              access: kb.access || 'on-demand',
            });
            kbEntries = kbEntries.slice(1);
          }
          while (mkEntries.length) {
            const mk = mkEntries[0];
            await saveOutreachEntry(mk.file, {
              title: mk.title,
              summary: mk.summary,
              when_to_attach: mk.when_to_attach,
            });
            mkEntries = mkEntries.slice(1);
          }
        }
      }
      if (isFund && !autoDraftsSaved) {
        // This is a separate, optional per-mailbox choice. Declining does not
        // block sync or manual generation, but records that the disclosure was
        // shown so the first Sync button need not ask again.
        await setFundAutoDrafts(autoDraftsChoice, FUND_AUTO_DRAFT_CONSENT_VERSION);
        autoDraftsSaved = true;
      }
      // Both account types welcome the user who just finished the wizard, at
      // their own address. The backend picks the wording from the organization
      // type and refuses a second welcome, so a Finish retry cannot resend it.
      try {
        const { sendWelcome } = await import('../lib/api');
        await sendWelcome();
      } catch (_) { /* non-critical */ }
      onComplete(userType === 'fund' ? 'fund' : 'sales');
    } catch (e: any) {
      uploadRecovery = isFund && setupSaved && (fundFiles.length + collateralFiles.length > 0);
      error = uploadRecovery
        ? `Your organization setup was saved, but ${fundFiles.length + collateralFiles.length} file${fundFiles.length + collateralFiles.length === 1 ? '' : 's'} did not finish uploading. Retry Finish, or continue and upload them later in Teach → Knowledge. ${e.message || ''}`
        : e.message || 'Failed to save';
      saving = false;
    }
  }

  function isValidUrl(s: string): boolean {
    const trimmed = s.trim();
    if (!trimmed) return false;
    try {
      const u = new URL(trimmed);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  function canNext(): boolean {
    // Consent is required even in preview, where the remaining fields are optional.
    if (step === 1) return displayName.trim().length > 0 && tosAccepted;
    if (step === 3 && userType === null) return false;
    if (preview) return true;
    switch (step) {
      case 2: return userBackground.trim().length > 0;
      // The fund branch moves product name/description to step 4, so step 3 is
      // just the management company's name.
      case 3: return isFund ? companyName.trim().length > 0
        : companyName.trim().length > 0 && productName.trim().length > 0;
      case 4: return isFund
        ? (targetLpTypes.length > 0 || !!customLpType.trim()) && funds.every(fund => !!fund.name.trim())
        : industry.trim().length > 0 && targetAudience.trim().length > 0;
      case 5: return selectedExperts.length > 0;
      case 6: return personaName.trim().length > 0;
      case 7: return backstory.trim().length > 0;
      case 8: return outreachObjective === 'call' || isValidUrl(selfServeUrl);
      case 9: return true;  // optional
      case 10: return true; // optional
      default: return false;
    }
  }

  function goNext() {
    if (!canNext()) return;
    if (isFund && step === 4) addCustomLpType();
    if (!isFund && step === 6 && personaName.trim() && !personaName.trim().includes(' ')) {
      personaName = personaName.trim() + ' Dealton';
    }
    stepIndex++;
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter' || e.isComposing || e.repeat || e.defaultPrevented || stepNumber >= totalSteps) return;
    const target = e.target;
    if (!(target instanceof HTMLElement) || !target.closest('.onboard-card')) return;

    // Enter advances from one-line fields. In textareas it still inserts a
    // newline; Ctrl/Command+Enter advances without changing the text.
    const singleLine = target instanceof HTMLInputElement
      && ['text', 'url'].includes(target.type)
      && !target.matches('.chip-input')
      && !target.closest('.custom-expert-row');
    const multilineShortcut = target instanceof HTMLTextAreaElement && (e.ctrlKey || e.metaKey);
    if (!singleLine && !multilineShortcut) return;
    e.preventDefault();
    goNext();
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#snippet autoDraftConsent()}
  <div class="fund-auto-consent">
    <label><input type="checkbox" bind:checked={autoDraftsChoice} />
      <span>Prepare review-only LP email drafts automatically</span></label>
    <p>Optional. After sync, Dealton may send up to eight relevant messages from your connected mailbox (up to 1,500 characters each) to Anthropic for a confirmed LP's reply or follow-up draft. You review every draft before choosing to send. No Fund documents are included. Change this in Settings any time. <a href="https://dealton.ai/terms.html" target="_blank" rel="noopener">Terms</a> · <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy</a>.</p>
  </div>
{/snippet}

<div class="onboard-wrap">
  <div class="onboard-card">
    {#if preview}
      <div class="onboard-preview-note" role="status">
        Setup preview · nothing here is saved.
        <button type="button" onclick={onExit}>Exit preview</button>
      </div>
    {/if}
    <div class="onboard-header">
      <div class="onboard-logo">Setup</div>
      <div class="onboard-progress">
        {#each Array(totalSteps) as _, i}
          <div class="dot" class:active={i + 1 === stepNumber} class:done={i + 1 < stepNumber}></div>
        {/each}
      </div>
      <div class="step-label">Step {stepNumber} of {totalSteps}</div>
    </div>

    <div class="onboard-body">
      {#if error}
        <div class="onboard-error">{error}</div>
        {#if uploadRecovery}<button class="onboard-recovery" type="button" disabled={saving} onclick={() => finish(true)}>Continue without remaining files</button>{/if}
      {/if}

      {#if step === 1}
        {#if mode === 'user'}
          <h2>Welcome to the team!</h2>
          <p class="hint">Your organization is already set up. Just confirm your name to get started.</p>
        {:else}
          <h2>Welcome! Let's get you set up.</h2>
        {/if}
        <label class="field-label" for="ob-displayName">Your name</label>
        <input id="ob-displayName" type="text" bind:value={displayName} placeholder="Your full name" />

        <label class="tos-check">
          <input type="checkbox" bind:checked={tosAccepted} required />
          <span>I agree to the <a href="https://dealton.ai/terms.html" target="_blank" rel="noopener">Terms of Service</a> and <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy Policy</a></span>
        </label>

      {:else if step === 2}
        <h2>Tell us about yourself</h2>
        <p class="hint">This helps your AI write in your voice.</p>
        <label class="field-label" for="ob-userBackground">Your background</label>
        <textarea id="ob-userBackground" bind:value={userBackground} rows="3"
          placeholder="Describe your role and the relationships you manage."></textarea>
        <label class="field-label" for="ob-userWritingStyle">Your writing style (optional)</label>
        <textarea id="ob-userWritingStyle" bind:value={userWritingStyle} rows="2"
          placeholder="e.g. Direct and detailed. I use specific facts, clear next steps, and avoid empty claims."></textarea>
        {#if mode === 'user' && isFund}{@render autoDraftConsent()}{/if}

      {:else if step === 3}
        <h2>Tell us about your organization</h2>
        {#if accountTypeLocked && !preview}
          <div class="workflow-current" role="status">
            <span class="workflow-current-label">Organization account</span>
            <strong>{isFund ? 'Fund · Investor Relations' : 'Corporate · Sales'}</strong>
            <span>This account type applies to everyone at your organization.</span>
          </div>
        {:else}
          <span class="field-label">How will your organization use Dealton?</span>
          <div class="workflow-choice" role="group" aria-label="Organization account type">
            <button type="button" class="workflow-opt" class:selected={userType === 'corporate'}
              aria-pressed={userType === 'corporate'}
              onclick={() => userType = 'corporate'}>
              <strong>Corporate <span>Sales</span></strong>
              <span>Manage buyers, deals and follow-ups.</span>
            </button>
            <button type="button" class="workflow-opt" class:selected={isFund}
              aria-pressed={isFund}
              onclick={() => userType = 'fund'}>
              <strong>Fund <span>Investor Relations</span></strong>
              <span>Manage LP relationships and portfolio context.</span>
            </button>
          </div>
        {/if}
        {#if isFund}
          <p class="hint">Use the management company name your LPs know. You can add fund vehicles on the next step.</p>
          <p class="hint">This is one team IR workspace. After teammates connect their inboxes, synced business relationship mail can support shared LP context and review-only briefs and drafts.</p>
          <label class="field-label" for="ob-companyName">Firm name</label>
          <input id="ob-companyName" type="text" bind:value={companyName} placeholder="e.g. Ridgeline Capital" />
        {:else if userType === 'corporate'}
          <p class="hint">This helps your AI partner understand what you sell.</p>
          <label class="field-label" for="ob-companyName">Company name</label>
          <input id="ob-companyName" type="text" bind:value={companyName} placeholder="e.g. GeoMet AI" />
          <label class="field-label" for="ob-productName">Product name</label>
          <input id="ob-productName" type="text" bind:value={productName} placeholder="e.g. GAUGE" />
          <label class="field-label" for="ob-productDescription">What does your product do?</label>
          <textarea id="ob-productDescription" bind:value={productDescription} rows="3" placeholder="Describe your product in 1-3 sentences..."></textarea>
        {/if}

      {:else if step === 4}
        {#if isFund}
          <h2>Who are your LPs?</h2>
          <p class="hint">Choose at least one investor type. This gives your IR partner useful context; you can refine it later.</p>
          <span class="field-label">Target LP types</span>
          <div class="chip-row">
            {#each LP_OPTIONS as opt}
              <button type="button" class="chip" class:selected={targetLpTypes.includes(opt)}
                aria-pressed={targetLpTypes.includes(opt)}
                onclick={() => targetLpTypes = toggleFrom(targetLpTypes, opt)}>{opt}</button>
            {/each}
            {#each targetLpTypes.filter(value => !LP_OPTIONS.includes(value)) as extra}
              <button type="button" class="chip selected" aria-pressed="true"
                onclick={() => targetLpTypes = toggleFrom(targetLpTypes, extra)}>{extra}</button>
            {/each}
            <input class="chip-input" aria-label="Add an LP type" bind:value={customLpType} placeholder="Add your own…"
              onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLpType(); } }}
              onblur={addCustomLpType} />
          </div>

          <span class="field-label">Investment focus (optional)</span>
          <div class="chip-row">
            {#each SECTOR_OPTIONS as opt}
              <button type="button" class="chip" class:selected={sectorFocus.includes(opt)}
                onclick={() => sectorFocus = toggleFrom(sectorFocus, opt)}>{opt}</button>
            {/each}
            {#each sectorFocus.filter(v => !SECTOR_OPTIONS.includes(v)) as extra}
              <button type="button" class="chip selected"
                onclick={() => sectorFocus = toggleFrom(sectorFocus, extra)}>{extra}</button>
            {/each}
            <input class="chip-input" bind:value={customSector} placeholder="Add your own…"
              onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSector(); } }}
              onblur={addCustomSector} />
          </div>

          <span class="field-label">Fund vehicles (optional)</span>
          <p class="hint">Your firm stays one IR workspace, even if it manages several funds. Add vehicle details only if useful now.</p>
          <div class="fund-list">
            {#each funds as fund, i (fund.id)}
              <div class="fund-entry">
                <div class="fund-entry-header">
                  <strong>Fund {i + 1}</strong>
                  <button type="button" class="fund-remove" aria-label={`Remove fund ${i + 1}`}
                    onclick={() => removeFund(fund.id)}>Remove</button>
                </div>
                <label class="field-label" for={`ob-fundName-${fund.id}`}>Fund name</label>
                <input id={`ob-fundName-${fund.id}`} type="text" bind:value={fund.name}
                  placeholder="e.g. Ridgeline Growth Fund II" />
                <label class="field-label" for={`ob-strategy-${fund.id}`}>Strategy (optional)</label>
                <textarea id={`ob-strategy-${fund.id}`} bind:value={fund.strategy} rows="3"
                  placeholder="Describe the fund's investment stage, sectors, geography, and approach."></textarea>
              </div>
            {/each}
          </div>
          <button type="button" class="add-fund" aria-label="Add fund vehicle" onclick={addFund}>+ Add fund vehicle</button>
          <label class="field-label" for="ob-fund-documents">Fund documents (optional)</label>
          <p class="hint">Upload fund decks, strategy notes, or reports. Originals are saved in your IR workspace for review and download.</p>
          <input id="ob-fund-documents" type="file" multiple disabled={preview}
            accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx,.jpg,.jpeg,.png,.webp"
            onchange={(event) => queueFundFiles(event, 'fund')} />
          {#each fundFiles as file, i}
            <div class="onboard-file-entry"><span>{file.name}</span><button type="button" onclick={() => fundFiles = fundFiles.filter((_, index) => index !== i)}>Remove</button></div>
          {/each}

        {:else}
          <h2>Who do you sell to?</h2>
          <p class="hint">Help your AI partner understand your buyers.</p>
          <label class="field-label" for="ob-industry">Industry</label>
          <input id="ob-industry" type="text" bind:value={industry} placeholder="e.g. Mining, Healthcare, SaaS" />
          <label class="field-label" for="ob-targetAudience">Customer profile</label>
          <textarea id="ob-targetAudience" bind:value={targetAudience} rows="3" placeholder="e.g. mining operators, SaaS companies, enterprise IT teams"></textarea>
        {/if}

        <div class="toggle-row">
          <!-- Same wording as Settings › Where George operates. Both read the
               same `personal_emails_as_clients` field; phrasing them opposite
               ways made the toggle look reversed between the two screens. -->
          <span class="toggle-label">{isFund ? 'Can personal emails be potential investors' : 'Treat personal emails as prospects'}</span>
          <label class="toggle-switch">
            <input
              type="checkbox"
              role="switch"
              aria-label={isFund ? 'Can personal emails be potential investors' : 'Treat personal emails as prospects'}
              checked={personalEmailsAsClients}
              onchange={(e) => personalEmailsAsClients = (e.currentTarget as HTMLInputElement).checked}
            />
            <span class="toggle-slider"></span>
          </label>
        </div>

      {:else if step === 5}
        <h2>What kind of expert is your AI?</h2>
        <p class="hint">Your AI partner will join sales calls as a domain expert. What expertise should they have?</p>
        <div class="expert-grid">
          {#each expertOptions as expert}
            <button
              class="expert-chip"
              class:selected={selectedExperts.includes(expert)}
              onclick={() => toggleExpert(expert)}
            >
              {expert}
            </button>
          {/each}
        </div>
        <div class="custom-expert-row">
          <input type="text" bind:value={customExpert} placeholder="Add custom..." onkeydown={(e) => e.key === 'Enter' && addCustomExpert()} />
          <button onclick={addCustomExpert} disabled={!customExpert.trim()}>Add</button>
        </div>
        {#if selectedExperts.length > 0}
          <div class="selected-list">
            <span class="field-label">Selected:</span>
            {#each selectedExperts as e}
              <span class="selected-tag">{e} <button class="tag-remove" onclick={() => toggleExpert(e)}>&times;</button></span>
            {/each}
          </div>
        {/if}

      {:else if step === 6}
        <h2>Name your AI partner</h2>
        <p class="hint">{isFund
          ? 'Your AI partner helps prepare LP calls and draft follow-up communications.'
          : 'This is the persona that will join your sales calls and assist with outreach.'}</p>
        <label class="field-label" for="ob-personaName">Partner name</label>
        <input id="ob-personaName" type="text" bind:value={personaName} placeholder="Enter a first name" />
        <p class="name-examples">For example: Alex, Maya, or Jordan.</p>

      {:else if step === 7}
        <h2>Who is {personaName || 'your partner'}?</h2>
        <p class="hint">Give {personaName || 'your AI partner'} a backstory. Choose an origin story:</p>

        <div class="mode-toggle">
          <button class="mode-btn" class:active={backstoryMode === 'dark'} onclick={() => switchMode('dark')}>
            <span class="mode-icon">&#128367;</span>
            Legacy
            <span class="mode-desc">A legendary expert whose knowledge lives on through AI</span>
          </button>
          <button class="mode-btn" class:active={backstoryMode === 'light'} onclick={() => switchMode('light')}>
            <span class="mode-icon">&#129309;</span>
            Partner
            <span class="mode-desc">A living expert who chose to scale their impact via AI</span>
          </button>
        </div>

        {#if generating}
          <div class="generating"><span class="spinner"></span> Generating backstory...</div>
        {:else if !hasGenerated}
          <button class="generate-btn" onclick={doGenerate} disabled={preview}>Generate Backstory</button>
        {/if}

        {#if hasGenerated && !generating}
          <label class="field-label" for="ob-backstory">Edit backstory</label>
          <textarea id="ob-backstory" bind:value={backstory} rows="8" class="backstory-edit"></textarea>
          <div class="regenerate-row">
            <button class="small-btn" onclick={doGenerate}>Regenerate</button>
          </div>
        {/if}

      {:else if step === 8}
        <h2>Outreach objective</h2>
        <p class="hint">What is the goal of cold outreach emails?</p>
        <div class="objective-grid">
          <button
            type="button"
            class="objective-card"
            class:selected={outreachObjective === 'call'}
            onclick={() => outreachObjective = 'call'}
          >
            <div class="objective-title">Book a call</div>
            <div class="objective-desc">Emails close with a low-friction ask for a brief call. Best when your sales motion is gated by a discovery conversation.</div>
          </button>
          <button
            type="button"
            class="objective-card"
            class:selected={outreachObjective === 'self_serve'}
            onclick={() => outreachObjective = 'self_serve'}
          >
            <div class="objective-title">Self-serve trial</div>
            <div class="objective-desc">Emails close with a self-serve invitation — sign up, spin up a workspace, try it on their data. Best when conversion happens in the product.</div>
          </button>
        </div>
        {#if outreachObjective === 'self_serve'}
          <label class="field-label" for="ob-selfServeUrl" style="margin-top:18px;">Sign-up / trial link <span style="color: #ef4444;">*</span></label>
          <input id="ob-selfServeUrl"
            type="url"
            bind:value={selfServeUrl}
            placeholder="https://yourproduct.com/signup"
            required
          />
          <p class="hint" style="margin-top:6px; margin-bottom:0;">
            {#if selfServeUrl.trim() && !isValidUrl(selfServeUrl)}
              <span style="color: #ef4444;">Enter a valid URL starting with http:// or https://</span>
            {:else}
              Pasted verbatim into the CTA of every self-serve email. Required.
            {/if}
          </p>
        {/if}

      {:else if step === 9}
        <h2>Knowledge Base</h2>
        <p class="hint">
          Upload documents about your product, technology, features, or competitive advantages.
          The AI extracts a summary for use in emails and calls.
          <strong>The original file is not stored</strong> — only the summary is kept.
        </p>
        <div class="mb-3">
          <input id="kb-onboard-file" type="file" disabled={preview} accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx"
            onchange={(e) => kbFile = (e.target as HTMLInputElement).files?.[0] ?? null} />
          <button class="primary mt-2" onclick={processKb} disabled={preview || !kbFile || kbProcessing}>
            {#if kbProcessing}<span class="spinner"></span> Processing...{:else}Process File{/if}
          </button>
        </div>
        {#if kbEntries.length > 0}
          <div class="text-xs text-dim mb-1">Queued knowledge entries ({kbEntries.length}):</div>
          {#each kbEntries as kb, i}
            <div class="onboard-file-entry">
              {#if kbEditIdx === i}
                <div style="flex:1;">
                  <input bind:value={kb.title} placeholder="Title" class="mb-1" />
                  <input bind:value={kb.when_to_use} placeholder="When to use this knowledge" class="mb-1" />
                  <textarea bind:value={kb.summary} rows="6" placeholder="Summary"></textarea>
                  <label class="text-xs mt-1" style="display:flex; align-items:center; gap:6px;">
                    <select bind:value={kb.access} style="font-size:11px; padding:2px 4px;">
                      <option value="on-demand">On-demand</option>
                      <option value="always">Always loaded</option>
                    </select>
                    {kb.access === 'always' ? 'Included in every AI prompt' : 'AI loads when relevant'}
                  </label>
                  <button class="text-xs mt-1" onclick={() => kbEditIdx = -1}>Done editing</button>
                </div>
              {:else}
                <div style="flex:1; min-width:0;">
                  <div class="font-bold truncate">{kb.title}</div>
                  <div class="text-xs text-dim">{kb.filename}</div>
                </div>
                <button class="text-xs" onclick={() => kbEditIdx = i}>Edit</button>
                <button class="text-xs danger" onclick={() => removeKb(i)}>Remove</button>
              {/if}
            </div>
          {/each}
        {/if}

      {:else if step === 10}
        {#if isFund}
          <h2>Investor Relations materials</h2>
          <p class="hint">Add LP updates, factsheets, or other materials your team may need. Originals are saved separately from Sales collateral.</p>
          <label class="field-label" for="ob-ir-collateral">IR materials (optional)</label>
          <input id="ob-ir-collateral" type="file" multiple disabled={preview}
            accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx,.jpg,.jpeg,.png,.webp"
            onchange={(event) => queueFundFiles(event, 'collateral')} />
          {#each collateralFiles as file, i}
            <div class="onboard-file-entry"><span>{file.name}</span><button type="button" onclick={() => collateralFiles = collateralFiles.filter((_, index) => index !== i)}>Remove</button></div>
          {/each}
          <p class="hint">{fundFiles.length} fund documents and {collateralFiles.length} IR materials queued.</p>
          {@render autoDraftConsent()}
        {:else}
        <h2>Marketing Materials</h2>
        <p class="hint">
          Upload sales collateral (one-pagers, brochures, case studies, pitch decks).
          The AI generates a summary and <strong>the original file is saved</strong> so it can be
          attached to outreach emails automatically.
        </p>
        <div class="mb-3">
          <input id="mk-onboard-file" type="file" disabled={preview} accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx,.jpg,.jpeg,.png,.webp"
            onchange={(e) => mkFile = (e.target as HTMLInputElement).files?.[0] ?? null} />
          <button class="primary mt-2" onclick={processMk} disabled={preview || !mkFile || mkProcessing}>
            {#if mkProcessing}<span class="spinner"></span> Processing...{:else}Process File{/if}
          </button>
        </div>
        {#if mkEntries.length > 0}
          <div class="text-xs text-dim mb-1">Queued marketing materials ({mkEntries.length}):</div>
          {#each mkEntries as mk, i}
            <div class="onboard-file-entry">
              {#if mkEditIdx === i}
                <div style="flex:1;">
                  <input bind:value={mk.title} placeholder="Title" class="mb-1" />
                  <input bind:value={mk.when_to_attach} placeholder="When to attach" class="mb-1" />
                  <textarea bind:value={mk.summary} rows="6" placeholder="Summary"></textarea>
                  <button class="text-xs mt-1" onclick={() => mkEditIdx = -1}>Done editing</button>
                </div>
              {:else}
                <div style="flex:1; min-width:0;">
                  <div class="font-bold truncate">{mk.title}</div>
                  <div class="text-xs text-dim">{mk.filename}</div>
                </div>
                <button class="text-xs" onclick={() => mkEditIdx = i}>Edit</button>
                <button class="text-xs danger" onclick={() => removeMk(i)}>Remove</button>
              {/if}
            </div>
          {/each}
        {/if}

        <div class="review-section">
          <h3>Review</h3>
          <div class="review-grid">
            <div class="review-label">Name</div><div class="review-val">{displayName}</div>
            <div class="review-label">Your role</div><div class="review-val">{userBackground.slice(0, 80)}{userBackground.length > 80 ? '...' : ''}</div>
            <div class="review-label">Company</div><div class="review-val">{companyName}</div>
            <div class="review-label">Product</div><div class="review-val">{productName}</div>
            <div class="review-label">Industry</div><div class="review-val">{industry}</div>
            <div class="review-label">Customer profile</div><div class="review-val">{targetAudience}</div>
            <div class="review-label">Experts</div><div class="review-val">{selectedExperts.join(', ')}</div>
            <div class="review-label">AI Partner</div><div class="review-val">{personaName} ({backstoryMode === 'dark' ? 'Legacy' : 'Partner'})</div>
            <div class="review-label">Knowledge</div><div class="review-val">{kbEntries.length} entries</div>
            <div class="review-label">Marketing</div><div class="review-val">{mkEntries.length} documents</div>
          </div>
        </div>
        {/if}
      {/if}
    </div>

    <div class="onboard-footer">
      {#if stepIndex > 0}
        <button class="back-btn" onclick={() => stepIndex--}>Back</button>
      {:else}
        <div></div>
      {/if}

      {#if stepNumber < totalSteps}
        <button class="next-btn" onclick={goNext} disabled={!canNext()}>
          {(step === 9 && kbEntries.length === 0) || (step === 10 && mkEntries.length === 0) ? 'Skip' : 'Next'}
        </button>
      {:else}
        <button class="next-btn finish" onclick={() => finish()} disabled={saving || !canNext()}>
          {#if saving}<span class="spinner"></span> Saving...{:else if preview}Exit preview{:else}Complete Setup{/if}
        </button>
      {/if}
    </div>
  </div>
</div>

<style>
  .fund-auto-consent { margin-top: 22px; padding: 14px 16px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--bg-hover); }
  .fund-auto-consent label { display: flex; align-items: center; gap: 10px; color: var(--text); font-weight: 600; font-size: 13px; cursor: pointer; }
  .fund-auto-consent input { accent-color: var(--accent); }
  .fund-auto-consent p { margin: 8px 0 0 24px; color: var(--text-muted); font-size: 11px; line-height: 1.5; }
  .fund-auto-consent a { color: var(--accent); }
  .onboard-preview-note { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 20px; background: var(--bg); border-bottom: 1px solid var(--border); color: var(--text-muted); font-size: 12px; }
  .onboard-preview-note button { border: 0; background: none; color: var(--accent); cursor: pointer; font: inherit; white-space: nowrap; }

  /* Organization account type is chosen once on the shared wizard's third step. */
  .workflow-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 8px 0 4px; }
  .workflow-opt {
    display: flex; flex-direction: column; align-items: flex-start; gap: 5px;
    padding: 14px 16px; text-align: left; cursor: pointer;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: var(--radius-sm); color: var(--text);
  }
  .workflow-opt:hover { border-color: var(--border-strong); background: var(--bg-hover); }
  .workflow-opt.selected { border-color: var(--accent); background: var(--accent-light); }
  .workflow-opt strong { font-size: 13px; font-weight: 600; }
  .workflow-opt strong span { margin-left: 5px; color: var(--text-muted); font-size: 11px; font-weight: 400; }
  .workflow-opt > span { font-size: 11px; color: var(--text-muted); line-height: 1.4; }
  .workflow-current { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; margin: 8px 0 22px; padding: 13px 16px; border-left: 2px solid var(--accent); background: var(--bg-hover); border-radius: 0 var(--radius-sm) var(--radius-sm) 0; }
  .workflow-current-label { color: var(--text-dim); font: 10px var(--font-mono); text-transform: uppercase; letter-spacing: .08em; }
  .workflow-current strong { font-size: 14px; font-weight: 600; }
  .workflow-current > span:last-child { color: var(--text-muted); font-size: 11px; }
  @media (max-width: 560px) { .workflow-choice { grid-template-columns: 1fr; } }

  .fund-list { display: grid; gap: 12px; margin: 12px 0; }
  .fund-entry {
    padding: 14px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg);
  }
  .fund-entry-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .fund-entry-header strong { font-size: 13px; color: var(--text); }
  .fund-entry .field-label { margin-top: 12px; }
  .fund-remove, .add-fund {
    border: 0;
    background: none;
    color: var(--accent);
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    padding: 4px 0;
  }
  .add-fund { margin-bottom: 8px; font-weight: 600; }
  .fund-remove:hover, .add-fund:hover { text-decoration: underline; }
  .fund-remove:focus-visible, .add-fund:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

  /* Multi-select chips (sector focus, target LPs). */
  .chip-row { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0 4px; align-items: center; }
  .chip {
    padding: 6px 11px; font-size: 12px; cursor: pointer;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 999px; color: var(--text-dim);
  }
  .chip:hover { border-color: var(--text-muted); }
  .chip.selected { border-color: var(--accent); color: var(--accent); background: var(--accent-light); }
  .chip-input {
    flex: 1; min-width: 120px; padding: 6px 11px; font-size: 12px;
    background: transparent; border: 1px dashed var(--border);
    border-radius: 999px; color: var(--text);
  }

  @media (max-width: 520px) { .workflow-choice { grid-template-columns: 1fr; } }

  .onboard-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
  }

  .onboard-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    width: 520px;
    max-width: 100%;
    overflow: hidden;
  }

  .onboard-header {
    padding: 24px 32px 20px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .onboard-logo {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-dim);
  }

  .onboard-progress {
    display: flex;
    gap: 6px;
    flex: 1;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--border);
    transition: all 0.2s;
  }
  .dot.active {
    background: var(--accent);
    transform: scale(1.3);
  }
  .dot.done {
    background: var(--green, #22c55e);
  }

  .step-label {
    font-size: 11px;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .onboard-body {
    padding: 28px 32px;
    min-height: 320px;
  }

  .onboard-body h2 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text);
    margin: 0 0 6px;
    letter-spacing: -0.01em;
  }
  .onboard-body h2 + .field-label { margin-top: 20px; }

  .hint {
    font-size: 13px;
    color: var(--text-muted);
    margin: 0 0 20px;
  }

  .name-examples {
    font-size: 12px;
    color: var(--text-muted);
    margin: 8px 0 0;
  }

  .objective-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .objective-card {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    text-align: left;
    padding: 18px 20px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    cursor: pointer;
    color: var(--text);
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }
  .objective-card:hover {
    border-color: var(--border-strong);
    background: var(--bg-hover);
  }
  .objective-card.selected {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 6%, var(--bg-surface));
    box-shadow: 0 0 0 1px var(--accent) inset;
  }
  .objective-card:active { transform: none; }
  .objective-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--text);
  }
  .objective-desc {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.5;
  }

  .field-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-dim);
    margin: 14px 0 5px;
  }
  .field-label:first-of-type {
    margin-top: 0;
  }

  .tos-check {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--text-muted);
    cursor: pointer;
  }
  .tos-check input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--accent, #e8553a);
    cursor: pointer;
    flex-shrink: 0;
  }
  .tos-check a {
    color: var(--accent, #e8553a);
    text-decoration: underline;
  }

  .toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
    padding: 14px 16px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg);
  }
  .toggle-label {
    font-size: 13px;
    color: var(--text);
  }
  /* Toggle now comes from app.css (.toggle-switch / .toggle-slider) — this
     page's scoped copy was a <button>+.toggle-knob variant at 40×22. */

  input[type="text"], textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg);
    color: var(--text);
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
  }
  input:focus, textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  .expert-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  .expert-chip {
    padding: 5px 12px;
    border-radius: 16px;
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .expert-chip:hover {
    border-color: var(--accent);
  }
  .expert-chip.selected {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }

  .custom-expert-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  .custom-expert-row input {
    flex: 1;
  }
  .custom-expert-row button {
    padding: 6px 14px;
    font-size: 12px;
  }

  .selected-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .selected-list .field-label { margin: 0; }

  .selected-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 12px;
    background: var(--bg-hover);
    font-size: 12px;
    color: var(--text);
  }

  .tag-remove {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    line-height: 1;
  }

  .mode-toggle {
    display: flex;
    gap: 10px;
    margin: 12px 0 16px;
  }

  .mode-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 12px;
    border-radius: var(--radius-sm);
    border: 2px solid var(--border);
    background: var(--bg);
    color: var(--text);
    cursor: pointer;
    transition: all 0.15s;
    font-size: 13px;
    font-weight: 500;
  }
  .mode-btn:hover {
    border-color: var(--accent);
  }
  .mode-btn.active {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 8%, var(--bg));
  }
  .mode-icon {
    font-size: 22px;
  }
  .mode-desc {
    font-size: 11px;
    color: var(--text-muted);
    font-weight: 400;
    text-align: center;
    line-height: 1.4;
  }

  .generating {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-dim);
    font-size: 13px;
    padding: 16px 0;
  }

  .generate-btn {
    width: 100%;
    padding: 10px;
    font-size: 13px;
    margin-top: 8px;
  }

  .backstory-edit {
    font-size: 13px;
    line-height: 1.5;
    min-height: 140px;
  }

  .regenerate-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 6px;
  }

  .small-btn {
    font-size: 11px;
    padding: 4px 10px;
    opacity: 0.7;
  }

  .review-section {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid var(--border);
  }

  .review-section h3 {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-dim);
    margin: 0 0 10px;
  }

  .review-grid {
    display: grid;
    grid-template-columns: 90px 1fr;
    gap: 4px 12px;
    font-size: 12px;
  }

  .review-label {
    color: var(--text-muted);
    font-weight: 500;
  }
  .review-val {
    color: var(--text);
    word-break: break-word;
  }

  .onboard-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 32px 20px;
    border-top: 1px solid var(--border);
  }

  .back-btn {
    font-size: 13px;
    padding: 8px 18px;
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text-dim);
    border-radius: var(--radius-sm);
    cursor: pointer;
  }
  .back-btn:hover {
    border-color: var(--text-muted);
  }

  .next-btn {
    font-size: 13px;
    padding: 8px 24px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-weight: 500;
    transition: opacity 0.15s;
  }
  .next-btn:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .next-btn.finish {
    padding: 8px 28px;
  }

  .onboard-error {
    background: color-mix(in srgb, var(--red) 8%, var(--bg-surface));
    color: var(--red);
    border: 1px solid color-mix(in srgb, var(--red) 20%, var(--border));
    border-radius: var(--radius-sm);
    padding: 9px 12px;
    font-size: 12px;
    margin-bottom: 14px;
  }
  .onboard-recovery {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg-surface);
    color: var(--text);
    padding: 8px 12px;
    margin-bottom: 14px;
    cursor: pointer;
  }
  .onboard-file-entry {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    margin-bottom: 8px;
    background: var(--bg);
  }
  .mt-2 { margin-top: 8px; }
</style>
